import React from "react";
import {
  FormControl,
  FormHelperText,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { getLocalTzLabel } from "../../services/dateUtils";

/**
 * Custom DateField component with enhanced functionality
 * Supports tooltips, validation, and custom styling
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Field label text
 * @param {string} props.name - Field name for form handling
 * @param {string|Date|moment} props.value - Current field value
 * @param {Function} props.onChange - Change handler function
 * @param {Function} props.onBlur - Blur handler function
 * @param {Function} props.setFieldTouched - Formik function to mark field as touched
 * @param {string} props.helperText - Helper text to display in tooltip
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.error - Error message string (from validation)
 * @param {boolean} props.touched - Whether the field has been touched
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {boolean} props.fullWidth - Whether the field should take full width
 * @param {string} props.format - Date format string (default: "MM/DD/YYYY")
 * @param {string} props.placeholder - Placeholder text
 * @param {Object} props.sx - Custom styling props
 * @param {Array} props.views - Available views for the date picker
 * @param {string} props.openTo - Initial view to open
 * @param {moment} props.minDate - Minimum selectable date
 * @param {moment} props.maxDate - Maximum selectable date
 * @param {boolean} props.showTimezoneLabel - Append local timezone label to field label
 */
const DateField = ({
  label,
  name,
  value = "",
  onChange,
  onBlur,
  setFieldTouched,
  helperText,
  required = false,
  error = "",
  touched = false,
  disabled = false,
  fullWidth = true,
  format = "MM/DD/YYYY",
  placeholder = "MM/DD/YYYY",
  sx = {},
  views = ["year", "month", "day"],
  openTo = "day",
  minDate,
  maxDate,
  showTimezoneLabel = false,
  ...otherProps
}) => {
  const timezoneLabel = showTimezoneLabel ? getLocalTzLabel() : "";
  const displayLabel =
    label && timezoneLabel ? `${label} (${timezoneLabel})` : label;

  /**
   * Renders the label with optional tooltip
   * If helperText is provided, shows an info icon with tooltip
   */
  const renderLabel = () => {
    if (!displayLabel) return null;

    return (
      <>
        {displayLabel}
        {helperText && (
          <Tooltip title={helperText} arrow>
            <IconButton size="small" sx={{ padding: 0, marginLeft: "4px" }}>
              <InfoIcon color="primary" fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  /**
   * Determine if field has error and should show error message
   * Error is shown only if field has been touched and has an error message
   */
  const hasError = touched && Boolean(error);
  const errorMessage = hasError ? error : "";

  /**
   * Convert value to moment object for the DatePicker
   */
  const getDateValue = () => {
    if (!value) return null;
    if (typeof value === "string") {
      // Handle different date string formats
      if (value.match(/^\d{8}$/)) {
        // Format: YYYYMMDD
        return moment(value, "YYYYMMDD");
      } else if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Format: YYYY-MM-DD
        return moment(value);
      } else {
        // Try to parse as is
        return moment(value);
      }
    }
    if (value instanceof Date) {
      return moment(value);
    }
    if (moment.isMoment(value)) {
      return value;
    }
    return null;
  };

  /**
   * Handle date change
   * Converts moment object back to string format for Formik
   */
  const handleDateChange = (newValue) => {
    if (onChange) {
      // Create a proper event object that Formik can handle
      const syntheticEvent = {
        target: {
          name: name,
          value: newValue ? newValue.format("YYYYMMDD") : "",
          type: "text",
        },
      };
      onChange(syntheticEvent);

      // Mark the field as touched when a date is selected
      if (setFieldTouched) {
        setFieldTouched(name, true);
      }
    }
  };

  /**
   * Handle field blur
   */
  const handleFieldBlur = () => {
    if (setFieldTouched) {
      setFieldTouched(name, true);
    } else if (onBlur) {
      const syntheticEvent = {
        target: {
          name: name,
        },
      };
      onBlur(syntheticEvent);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <FormControl
        error={hasError}
        disabled={disabled}
        fullWidth={fullWidth}
        sx={sx}
      >
        <DatePicker
          label={renderLabel()}
          value={getDateValue()}
          onChange={handleDateChange}
          onAccept={handleFieldBlur}
          format={format}
          placeholder={placeholder}
          views={views}
          openTo={openTo}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          slotProps={{
            textField: {
              required: required,
              fullWidth: fullWidth,
              error: hasError,
              InputLabelProps: {
                sx: {
                  "& .MuiFormLabel-asterisk": {
                    color: "red",
                    fontSize: "1.2rem",
                  },
                },
              },
            },
          }}
          {...otherProps}
        />
        {hasError && <FormHelperText error>{errorMessage}</FormHelperText>}
      </FormControl>
    </LocalizationProvider>
  );
};

export default DateField;
